/**
 * Forgot Password Page Component
 * Allows users to request a password reset via email
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, X, Mail, ArrowLeft } from 'lucide-react';
import customAuthService from '../../services/customAuthService';
import { validateEmail } from '../../utils/validation';
import { useTranslationNamespace } from '../../i18n/hooks/useTranslationNamespace';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationHelper';
import '../../styles/auth/CustomLoginPage.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  // Use lazy-loading hook to ensure login namespace is fully loaded
  const { t } = useTranslationNamespace('login');

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldError, setFieldError] = useState('');

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldError('');

    // Validate email
    const validation = validateEmail(email, t);
    if (!validation.isValid) {
      setFieldError(validation.error);
      return;
    }

    setLoading(true);

    try {
      await customAuthService.requestPasswordReset(email);
      setSuccess(true);
      showSuccessNotification(t('resetCodeSent'));

      // Navigate to verify code page after 2 seconds
      setTimeout(() => {
        navigate('/verify-code', { state: { email } });
      }, 2000);
    } catch (err) {
      // Use translation key from API response if available, otherwise fallback to generic message
      const errorMessageKey = err.response?.data?.message;
      const errorMessage = errorMessageKey ? t(errorMessageKey) : (err.message || t('resetFailed'));
      setError(errorMessage);
      showErrorNotification(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    setEmail(e.target.value);
    if (fieldError) {
      setFieldError('');
    }
    if (error) {
      setError('');
    }
  };

  return (
    <div className="custom-login-page">
      {/* Header */}
      <header className="custom-login-header">
        <Link to="/" className="custom-login-logo">
          <img
            src="/logo.svg"
            alt="Promotly Logo"
            className="custom-login-logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <span className="custom-login-logo-text">Promotly</span>
        </Link>
      </header>

      {/* Main Container */}
      <div className="custom-login-container">
        {/* Form Section (Centered) */}
        <div className="custom-login-form-section" style={{ gridColumn: '1 / -1' }}>
          <div className="custom-login-form-wrapper">
            {/* Back Button */}
            <Link
              to="/login"
              className="custom-login-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}
            >
              <ArrowLeft size={16} style={{ transform: document.documentElement.dir === 'rtl' ? 'rotate(180deg)' : 'none' }} />
              {t('back')}
            </Link>

            {/* Title */}
            <h1 className="custom-login-title">{t('forgotPasswordTitle')}</h1>
            <p className="custom-login-subtitle">
              {success
                ? t('resetCodeSent')
                : t('forgotPasswordSubtitle')}
            </p>

            {/* Error Alert */}
            {error && (
              <div className="custom-login-error-alert" role="alert">
                <AlertCircle className="custom-login-error-icon" size={18} />
                <span>{error}</span>
                <button
                  className="custom-login-error-dismiss"
                  onClick={() => setError('')}
                  aria-label="Dismiss error"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div
                className="custom-login-error-alert"
                style={{ background: '#d1fae5', borderColor: '#6ee7b7', color: '#065f46' }}
                role="status"
              >
                <Mail className="custom-login-error-icon" size={18} />
                <span>{t('resetCodeSent')}. {t('sending')}</span>
              </div>
            )}

            {/* Form */}
            {!success && (
              <form className="custom-login-form" onSubmit={handleSubmit} noValidate>
                {/* Email Field */}
                <div className="custom-login-form-group">
                  <label htmlFor="email" className="custom-login-label">
                    {t('emailAddress')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className={`custom-login-input ${fieldError ? 'error' : ''}`}
                    placeholder={t('enterYourEmail')}
                    value={email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="email"
                    autoFocus
                    aria-invalid={!!fieldError}
                    aria-describedby={fieldError ? 'email-error' : undefined}
                    required
                  />
                  {fieldError && (
                    <span id="email-error" className="custom-login-field-error" role="alert">
                      {fieldError}
                    </span>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="custom-login-button"
                  disabled={loading}
                  aria-busy={loading}
                >
                  {loading ? (
                    <>
                      <span className="custom-login-spinner" aria-hidden="true"></span>
                      <span>{t('sending')}</span>
                    </>
                  ) : (
                    <span>{t('sendResetCode')}</span>
                  )}
                </button>
              </form>
            )}

            {/* Footer */}
            <div className="custom-login-footer">
              <p style={{ marginTop: '16px', marginBottom: '0' }}>
                {t('rememberPassword')}{' '}
                <Link to="/login" className="custom-login-link" style={{ marginInlineStart: '8px' }}>
                  {t('login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;